import { useEffect } from 'react';
import { Form, Input, Select, Button } from '@/shared/ui/primitives';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { regionApi } from '../api/region.api';
import { regionKeys } from '../api/region.keys';
import type { Region, RegionFormData } from '@/shared/types';

interface RegionFormProps {
  initialData?: Region;
  onSubmit: (data: RegionFormData) => void;
  isLoading: boolean;
}

export default function RegionForm({ initialData, onSubmit, isLoading }: RegionFormProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm<RegionFormData>();

  const { data: countries = [], isLoading: loadingCountries } = useQuery({
    queryKey: regionKeys.countries,
    queryFn: () => regionApi.listCountries(),
  });

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        nameEn: initialData.nameEn || '',
        nameAr: initialData.nameAr || '',
        countryId: initialData.countryId || undefined,
      });
    }
  }, [initialData, form]);

  return (
    <Form form={form} layout="vertical" onFinish={onSubmit} style={{ maxWidth: 480 }}>
      <Form.Item name="nameEn" label={t('regions.nameEn')} rules={[{ required: true }]}>
        <Input placeholder={t('regions.nameEnPlaceholder')} />
      </Form.Item>
      <Form.Item name="nameAr" label={t('regions.nameAr')} rules={[{ required: true }]}>
        <Input dir="rtl" placeholder={t('regions.nameArPlaceholder')} />
      </Form.Item>
      <Form.Item name="countryId" label={t('regions.country')} rules={[{ required: true }]}>
        <Select placeholder={t('regions.selectCountry')} loading={loadingCountries} options={countries.map((c) => ({ value: c.id, label: c.nameEn }))} />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={isLoading}>{t('common.save')}</Button>
      </Form.Item>
    </Form>
  );
}
